import React, { PropsWithChildren, createContext, useContext, useState } from 'react';
import { EntryType, ORDER_METHODS } from '../../../services/exports/Constants';
import { CompanyDetailsInterface, OrderData, ProfileInterface } from '../../../services/exports/Interfaces';
import { useSelector } from 'react-redux';
import { selectCompany } from '../../../store/slices/InitialDataSlice';
import { selectCachedOrder } from '../../../store/slices/OrderSlice';
import { setTableNotice } from '../../../store/slices/OrderMethodSlice';
import useRouter from '../../../hooks/navigation/useRouter';
import useAppMethods from '../../../hooks/utility/useAppMethods';
import useOrderMethodsStatus from '../../../hooks/availability/useOrderMethodsStatus';
import { actionCreators } from '../../../store/actions';
import useDeliveryForm from '../../../modules/mobile/modals/delivery-address/form/useDeliveryForm';
import useDeliveryMethodValidation from './useDeliveryMethodValidation';
import { DeliveryAddressSchema } from '../../../modules/mobile/modals/delivery-address/form/schema';
import ProfileManager from '../../../services/api/ProfileManager';
import { useTranslation } from 'react-i18next';
import { FormProvider } from 'react-hook-form';
import AuthorizationTokenStorage from '../../../services/auth/AuthorizationTokenStorage';
import ReduxActions from '../../../store/ReduxActions';
import useCompanySlug from '../../../hooks/global/useCompanySlug';
import useSideEffects from '../../../hooks/global/useSideEffects';
import useOrderFlow from '../../../hooks/order/useOrderFlow';
import { useLazyGetCompanyMenusDataQuery } from '../../../store/api-slices/CompaniesApiSlice';
import useToast from '../../../hooks/utility/useToast';
import { BasketLineItemModifier, StoreInterface } from '../../../store/types';
import ChannelDropToast from '../../../components-library/ChannelDropToast';
import useOrderMethodModalGate from '../../../hooks/order/useOrderMethodModalGate';
import { ValidationErrors } from '../types';
import { mailableLanguage } from '../../../services/localization/Languages';

interface LocationContextType {
  loading: boolean;
  orderMethod: ORDER_METHODS;
  company?: CompanyDetailsInterface;
  scheduledFor?: string;
  setOrderMethod: (orderMethod: ORDER_METHODS) => void;
  setCompany: (company: CompanyDetailsInterface) => void;
  setScheduledFor: (scheduledFor?: string) => void;
  save: () => void;
  validationError?: ValidationErrors;
  masterPromoCode?: string;
  resetError: () => void;
  setNoCouriersError: (masterPromoCode?: string) => void;
}

const LocationDataProviderContext = createContext<LocationContextType | undefined>({} as LocationContextType);

const LocationDataProvider = ({ children }: PropsWithChildren) => {
  const { i18n, t } = useTranslation(null, { keyPrefix: 'Modals:OrderMethod:OrderMethodModal' });
  const selectedCompany = useSelector(selectCompany);
  const cachedOrder = useSelector(selectCachedOrder);
  const { lineItems } = useSelector((store: StoreInterface) => store.basket);
  const [triggerGetMenus] = useLazyGetCompanyMenusDataQuery();
  const toast = useToast();
  const { updateSlug, loading: isCompanySlugLoading } = useCompanySlug();
  const { toggleOrderMethodModal } = useAppMethods();
  const { setLoading } = useSideEffects();
  const { navigate } = useRouter();
  const { setOrderMethodInfoInfoComplete } = useOrderFlow();
  const { clearDismissed } = useOrderMethodModalGate();
  const [company, setCompany] = useState<CompanyDetailsInterface>(null);
  const { visibleOrderMethods, takeoutCurrentlyOpen, takeoutOpenForFutureOrders } = useOrderMethodsStatus(company);
  // A restaurant that only takes table orders shows no tab here (only its QR codes start them); the
  // cart then keeps a method rather than none.
  const orderMethod = visibleOrderMethods.includes(cachedOrder?.method)
    ? cachedOrder.method
    : (visibleOrderMethods[0] ?? ORDER_METHODS.PICKUP);
  const [isDeliveryAddressFormEmpty] = useState(() => orderMethod === ORDER_METHODS.PICKUP);
  const deliveryAddressForm = useDeliveryForm({ empty: isDeliveryAddressFormEmpty });
  const [state, setState] = useState<Pick<LocationContextType, 'orderMethod' | 'scheduledFor'>>({
    orderMethod,
  });
  const setOrderMethod = (orderMethod: ORDER_METHODS) => {
    resetError();
    setState(({ scheduledFor: _, ...prev }) => ({ ...prev, orderMethod }));
  };

  const setScheduledFor = (scheduledFor: string) => {
    setState((prev) => ({ ...prev, scheduledFor }));
  };

  const { validate, loading, validationError, masterPromoCode, resetError, setNoCouriersError } =
    useDeliveryMethodValidation({
      company,
      scheduledFor: state.scheduledFor,
    });
  const cacheInfoRedux = (data: Partial<OrderData> = {}) => {
    ReduxActions.dispatch(
      actionCreators.order.updateOrder({
        company_id: company?.id,
        method: state.orderMethod,
        scheduled_for: state.scheduledFor,
        ...deliveryAddressForm.getValues(),
        ...data,
      }),
    );
  };

  const pruneBasketForChannel = async (newMethod: ORDER_METHODS) => {
    if (newMethod !== ORDER_METHODS.PICKUP && newMethod !== ORDER_METHODS.DELIVERY) {
      return;
    }

    if (!company?.id) {
      return;
    }

    if (cachedOrder?.company_id === company.id && cachedOrder?.method === newMethod) {
      return;
    }

    const basketItems = lineItems[company.id] ?? [];

    if (basketItems.length === 0) {
      return;
    }

    const menus = await triggerGetMenus(company.id, true)
      .unwrap()
      .catch(() => null);
    const products = menus?.products ?? {};
    const modifiers = menus?.modifiers ?? {};
    const channelField = newMethod === ORDER_METHODS.PICKUP ? 'is_available_for_pickup' : 'is_available_for_delivery';

    const isUnavailable = (entry: {
      is_available_for_pickup?: boolean;
      is_available_for_delivery?: boolean;
    }): boolean => entry != null && entry[channelField] === false;

    // A modifier line can be a nested product (meal deal) or a plain modifier — check the right map,
    // and recurse into nested modifiers so a deeply-nested unavailable option still drops the item.
    const hasUnavailableModifier = (lineModifiers: BasketLineItemModifier[] = []): boolean =>
      lineModifiers.some((modifier) => {
        const source = modifier.modifier_type === EntryType.Product ? products : modifiers;

        if (isUnavailable(source[modifier.modifier_id])) {
          return true;
        }

        return hasUnavailableModifier(modifier.modifiers);
      });

    const survivingItems = basketItems.filter(
      (item) => !isUnavailable(products[item.product_id]) && !hasUnavailableModifier(item.modifiers),
    );
    const droppedCount = basketItems.length - survivingItems.length;

    if (droppedCount === 0) {
      return;
    }

    ReduxActions.dispatch(
      actionCreators.basket.set({
        company_id: company.id,
        items: survivingItems,
      }),
    );

    const channelKey = newMethod === ORDER_METHODS.PICKUP ? 'pickup' : 'delivery';
    const countLabel = t('items_dropped.count', { count: droppedCount });
    const reasonLabel = t(`items_dropped.${channelKey}_reason`);

    toast.custom((toastInstance) => (
      <ChannelDropToast t={toastInstance} channel={newMethod} countLabel={countLabel} reasonLabel={reasonLabel} />
    ));
  };

  const handleSuccessSave = async (data: Partial<OrderData> = {}) => {
    setLoading(true);

    const newMethod = (data?.method as ORDER_METHODS) ?? state.orderMethod;
    await pruneBasketForChannel(newMethod);

    if (selectedCompany?.id !== company?.id) {
      await updateSlug(company?.slug);
      navigate({
        web: {
          to: company?.slug,
          options: { absolute: true },
        },
      });
    }

    await updateProfile();

    cacheInfoRedux(data);
    // The guest confirmed a method, so a note about the table they're not at no longer applies.
    ReduxActions.dispatch(setTableNotice(null));

    setOrderMethodInfoInfoComplete();
    clearDismissed();
    toggleOrderMethodModal({ openModal: false });
    setLoading(false);
  };

  const handleDeliveryDetailsValidation = async (data: DeliveryAddressSchema) => {
    const validatedData = await validate(data);

    if (!validatedData) {
      return;
    }

    // The profile is saved from the form values, so the resolved country has to land there too.
    deliveryAddressForm.setValue('delivery_country', validatedData.delivery_country ?? null);

    await handleSuccessSave(validatedData);
  };

  async function updateProfile(data: Partial<ProfileInterface> = {}) {
    if (!AuthorizationTokenStorage.getToken()) {
      return;
    }

    await ProfileManager.putProfile(company?.id, {
      ...deliveryAddressForm.getValues(),
      lang: mailableLanguage(i18n.language),
      ...data,
    });
  }

  const save = async () => {
    if (!takeoutCurrentlyOpen && !takeoutOpenForFutureOrders) {
      toggleOrderMethodModal({ openModal: false });

      return;
    }

    if (state.orderMethod === ORDER_METHODS.DELIVERY) {
      return deliveryAddressForm.handleSubmit(handleDeliveryDetailsValidation)();
    }

    await handleSuccessSave();
  };

  const contextValue: LocationContextType = {
    loading: loading || isCompanySlugLoading,
    ...state,
    company,
    setOrderMethod,
    setCompany: (company) => {
      setState(({ scheduledFor: _, ...rest }) => rest);
      setCompany(company);
    },
    setScheduledFor,
    save,
    validationError,
    masterPromoCode,
    resetError,
    setNoCouriersError,
  };

  return (
    <>
      <FormProvider {...deliveryAddressForm}>
        <LocationDataProviderContext.Provider value={contextValue}>{children}</LocationDataProviderContext.Provider>
      </FormProvider>
    </>
  );
};

export const useLocationData = () => useContext(LocationDataProviderContext);

export default LocationDataProvider;
